"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IPAddressFieldLogic = void 0;
const differenceBy_1 = __importDefault(require("lodash-es/differenceBy"));
const isString_1 = __importDefault(require("lodash-es/isString"));
const types_1 = require("@smartsuite/types");
const ip_address_1 = require("../../filter/ip-address");
const clipboard_1 = require("../../clipboard");
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const fields_1 = require("../../utils/fields");
const google_sheet_1 = require("../../utils/google-sheet");
const migration_1 = require("../../utils/migration");
const text_1 = require("../../utils/text");
const ip_address_fieldtype_1 = require("../../safe-field-value/text-fields/ip-address-field/ip-address.fieldtype");
const filter_1 = require("../../utils/filter");
const validation_1 = require("../../validation/validation");
const texts_1 = require("../../texts");
const intl_1 = require("../../locale/intl");
const bulk_update_1 = require("../../utils/bulk-update");
class IPAddressFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "ipaddressfield";
        this.label = (0, intl_1.$t)(texts_1.ipAddressFieldText.ip_address);
        this.icon = "internet-protocol";
        this.iconColor = "#3A86FF";
        this.placeholder = (0, intl_1.$t)(texts_1.ipAddressFieldText.enter_ip_address);
        this.searchSynonyms = [
            (0, intl_1.$t)(texts_1.ipAddressFieldText.network),
            (0, intl_1.$t)(texts_1.ipAddressFieldText.internet),
        ];
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: ip_address_1.isPredicateGenerator,
            is_not: ip_address_1.isNotPredicateGenerator,
            is_empty: comparisons_1.isEmptyList,
            is_not_empty: comparisons_1.isNotEmptyList,
            contains: ip_address_1.containsPredicateGenerator,
            not_contains: ip_address_1.notContainsPredicateGenerator,
        };
        this.fieldValueGetter = new ip_address_fieldtype_1.IpAddressFieldValueGetter();
        this.clipboardPasteRules = {
            ipaddressfield: clipboard_1.clipboardIpAddressSelf,
            linkedrecordfield: clipboard_1.clipboardReferenceFieldToIP,
            textfield: clipboard_1.clipboardTextToIpAddress,
            recordtitlefield: clipboard_1.clipboardTextToIpAddress,
            textareafield: clipboard_1.clipboardTextToIpAddress,
            richtextareafield: clipboard_1.clipboardTextToIpAddress,
            tagsfield: clipboard_1.clipboardTextToIpAddress,
            unknownfield: clipboard_1.clipboardTextToIpAddress,
        };
    }
    get validators() {
        return { requiredIpList: validation_1.requiredIpList, ipAddressField: validation_1.ipAddressField };
    }
    valueToString(fieldValue, _field, ..._rest) {
        if (typeof fieldValue === "string") {
            return fieldValue;
        }
        return (fieldValue || []).map((item) => item.address).join(" ");
    }
    valueToGoogleSheet(fieldValue, field) {
        if (this.isValueEmpty(fieldValue)) {
            return (0, google_sheet_1.makeRowDataValues)("", "stringValue");
        }
        const value = fieldValue.map((item) => item.address).join(", ");
        return (0, google_sheet_1.makeRowDataValues)(value, "stringValue");
    }
    valueToExport(fieldValue, _field, ..._rest) {
        if ((0, isString_1.default)(fieldValue)) {
            return fieldValue;
        }
        return fieldValue.map((item) => item.address).join(",");
    }
    filterValueToString(value, field, ..._rest) {
        if (typeof value === "string") {
            return value;
        }
        return this.valueToString(value, field, ..._rest);
    }
    processFieldValue(record, slug, comparison, field) {
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        return fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.map((ipAddress) => ipAddress.address);
    }
    processSearch(searchTerm, fieldValue, field, userPreferences, record) {
        const preparedFieldValue = this.searchKey(fieldValue, field, userPreferences, record);
        return (0, text_1.isMultipleTermInSearch)(searchTerm, preparedFieldValue);
    }
    getInlineEditRules(field) {
        const isSingle = field.params.entries_allowed === "single";
        const baseInlineEditRules = super.getInlineEditRules(field);
        const switchToInputControl = isSingle
            ? "OnSelectedCellClick"
            : "OnCellSelect";
        const cellPresenceEventMode = isSingle ? "OnEdit" : "OnModalOpen";
        return {
            ...baseInlineEditRules,
            closeWhenClickOutside: isSingle,
            deleteValueOnFirstKeypress: isSingle,
            switchToInputControl,
            editorWidth: "100%",
            enableCopyEventInEditMode: !isSingle,
            enablePasteEventInEditMode: !isSingle,
            saveKeys: isSingle ? baseInlineEditRules.saveKeys : types_1.navigationKeys,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode,
            needUseDefaultValue: (value, isDirty) => {
                const isNeedUseDefaultValue = (0, fields_1.needUseDefaultFieldValue)(field, value);
                if (isSingle) {
                    return isNeedUseDefaultValue;
                }
                return isDirty && isNeedUseDefaultValue;
            },
            spaceKeyOpensRecordModal: (isEditMode) => !isEditMode || !isSingle,
        };
    }
    getEmptyValue() {
        return [];
    }
    getInitialFieldValue(startValue) {
        return startValue
            ? [{ address: startValue, country_code: "" }]
            : this.getEmptyValue();
    }
    getGroupValues(fieldValue) {
        if (!Array.isArray(fieldValue) || !fieldValue.length) {
            return [[]];
        }
        return fieldValue
            .filter(filter_1.filterUniqueValues)
            .map((valueItem) => [valueItem]);
    }
    prepareValueForClipboard(fieldValue, _field) {
        return {
            text: fieldValue.map((ip) => ip === null || ip === void 0 ? void 0 : ip.address).join(" ; "),
            rawValue: fieldValue,
        };
    }
    isValueEmpty(fieldValue) {
        if (!Array.isArray(fieldValue) || !fieldValue.length) {
            return true;
        }
        return fieldValue.every((ip) => !ip.address);
    }
    formatActivityRecordChangeValues(previousValue, currentValue) {
        if (this.isValueEmpty(previousValue) || this.isValueEmpty(currentValue)) {
            return { previousValue, currentValue };
        }
        const removedValues = (0, differenceBy_1.default)(previousValue, currentValue, "address");
        const addedValues = (0, differenceBy_1.default)(currentValue, previousValue, "address");
        return { previousValue: removedValues, currentValue: addedValues };
    }
    replaceValue(field, record, sourceValue = [], targetValue = [], _userPreferences) {
        return (0, fields_1.replaceValueInList)(field, record, sourceValue, targetValue);
    }
    getLinkedRecordFieldSmartWidth(field) {
        const widthOptions = {
            single: {
                defaultWidht: 120,
                minWidth: 60,
            },
            multiple: {
                defaultWidht: 170,
                minWidth: 90,
            },
        };
        const entriesAllowed = field.params.entries_allowed;
        const width = widthOptions[entriesAllowed];
        return {
            defaultWidht: width.defaultWidht,
            minWidth: width.minWidth,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.ipAddressDisplayFormats, (_a = fieldTo.params.display_format) !== null && _a !== void 0 ? _a : "");
    }
    filterToRecordValue(filterValue, comparison, field) {
        if (comparison !== "is" || !filterValue) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const values = filterValue.split(types_1.delimetersRegex);
        const addresses = values
            .filter((address) => types_1.ipv4Ipv6Regex.test(address))
            .map((address) => ({ address, country_code: "" }));
        if (values.length !== addresses.length) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        return addresses;
    }
    /**
     * Applies a bulk update to a field value.
     */
    applyBulkUpdate(field, value, update) {
        if (field.params.entries_allowed === "single") {
            return super.applyBulkUpdate(field, value, update);
        }
        return (0, bulk_update_1.applyBulkUpdateToDictsArray)(value, update, "address");
    }
}
exports.IPAddressFieldLogic = IPAddressFieldLogic;
